import { useState, useEffect, useRef } from 'react';
import { Trash2, Loader2, Send, ExternalLink, CheckCircle2, AlertCircle } from 'lucide-react';
import { todayISO } from './lib/formatters';
import { Button } from './components/ui/Button';
import { Modal } from './components/ui/Modal';
import { Field } from './components/ui/Field';
import { FileInput } from './components/ui/FileInput';
import { Toast } from './components/ui/Toast';
import { sendNFEmitidaEmail } from './lib/email';
import { logEvento, EVENTOS } from './lib/audit';
import { openMailtoNFEmitida } from './lib/email-text';
import { validateEmailList } from './lib/formatters';
import { Sidebar, SIDEBAR_COLLAPSED_WIDTH } from './components/Sidebar';
import { AppHeader } from './components/AppHeader';
import { PDFEspelho } from './components/PDFEspelho';
import { DetailsModal } from './components/DetailsModal';
import { CNPJEntityForm } from './forms/CNPJEntityForm';
import { ServicoForm } from './forms/ServicoForm';
import { UnidadeNegocioForm } from './forms/UnidadeNegocioForm';
import { HomeView } from './views/HomeView';
import { SolicitacaoForm, addMonthsISO } from './views/SolicitacaoForm';
import { EmpresasView } from './views/EmpresasView';
import { UnidadesView } from './views/UnidadesView';
import { TomadoresView } from './views/TomadoresView';
import { ServicosView } from './views/ServicosView';
import { CambioHome } from './views/CambioHome';
import { CambioForm } from './views/CambioForm';
import { CambioDetailsModal } from './components/CambioDetailsModal';
import { LoginView } from './views/LoginView';
import { AccessDenied } from './views/AccessDenied';
import { AccessDeniedDomain } from './views/AccessDeniedDomain';
import { useEmpresas } from './hooks/useEmpresas';
import { useTomadores } from './hooks/useTomadores';
import { useServicos } from './hooks/useServicos';
import { useSolicitacoes } from './hooks/useSolicitacoes';
import { useUnidadesNegocio } from './hooks/useUnidadesNegocio';
import { useCambio } from './hooks/useCambio';
import { useToast } from './hooks/useToast';
import { useSession } from './hooks/useSession';
import { useReplica, useReplicaSlug } from './hooks/useReplica';
import { getReplicaConfig } from './lib/replicas.config';
import { navigate } from './lib/routing';
import { applyAppTheme, clearAppTheme } from './lib/app-theme';
import { LEGACY_APPS, PRINCIPAL_ONBOARDING_URL } from './lib/constants';

export default function App() {
  const auth = useSession();

  // Tema do app baseado no slug da URL (config-driven). Roda ANTES do
  // login, entao a tela de login e a de "acesso negado" tambem ficam na
  // cor da empresa. Quando logado, o useEffect dentro de AuthedApp pode
  // sobrescrever com o tema_cor real do DB (autoritativo).
  const slug = useReplicaSlug();
  useEffect(() => {
    const cfg = getReplicaConfig(slug);
    if (cfg?.temaCor) applyAppTheme(cfg.temaCor);
    else clearAppTheme();
  }, [slug]);



  if (auth.loading || (auth.session && !auth.accessChecked)) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg)', color: 'var(--muted)',
      }}>
        <Loader2 size={20} style={{ animation: 'fileinput-spin 1s linear infinite' }} />
      </div>
    );
  }

  // Camada 1: dominio nao permitido (vem do useSession, depois de signOut).
  // Mostra ANTES das outras checagens — quem caiu aqui ja perdeu a sessao
  // e so consegue sair clicando 'Tentar com outra conta' (recarrega a pagina).
  if (auth.deniedByDomain) return <AccessDeniedDomain email={auth.deniedByDomain} />;

  if (!auth.session) return <LoginView />;
  if (!auth.hasAccess) return <AccessDenied email={auth.session.user?.email} />;

  return <AuthedApp session={auth.session} isAdmin={auth.isAdmin} scopes={auth.scopes} />;
}

function AuthedApp({ session, isAdmin, scopes }) {
  // ---------------------------------------------------------------
  // 1) Data hooks (fetchers) — todos no topo pra nao quebrar a ordem
  // ---------------------------------------------------------------
  const empresasH     = useEmpresas();
  const tomadoresH    = useTomadores();
  const servicosH     = useServicos();
  const solicitacoesH = useSolicitacoes();
  const cambioH       = useCambio();
  const unidadesH     = useUnidadesNegocio();
  const { toasts, toast, dismiss } = useToast();

  // ---------------------------------------------------------------
  // 2) useState / useRef — declaracoes ANTES de qualquer useEffect.
  //    (Antes estavam intercaladas e o minify gerava TDZ:
  //     "Cannot access 'C' before initialization".)
  // ---------------------------------------------------------------
  const [view, setView] = useState('home');
  const [editingSolic, setEditingSolic] = useState(null);
  const [modal, setModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Fechamento de cambio (LLCs): estado da view de cambio
  const [editingCambio, setEditingCambio] = useState(null);
  const [cambioModal, setCambioModal] = useState(null);
  const [cambioSubView, setCambioSubView] = useState('home'); // 'home' | 'form'
  // Quando o usuario clica '+ Novo tomador' dentro do form de solicitacao,
  // a gente abre o modal de cadastro. Apos salvar, guarda o id aqui pra
  // o SolicitacaoForm capturar e auto-selecionar o tomador recem-criado.
  const [newlyCreatedTomadorId, setNewlyCreatedTomadorId] = useState(null);

  // Estado do modal "Enviar NF emitida por e-mail"
  const [sendNFRecipients, setSendNFRecipients] = useState('');
  const [sendNFSending, setSendNFSending] = useState(false);
  const [sendNFResult, setSendNFResult] = useState(null);

  const autoAtrasadoRanRef = useRef(false);

  // ---------------------------------------------------------------
  // 3) Dados crus dos hooks + replica ativa + flags de acesso
  // ---------------------------------------------------------------
  const empresasAll     = empresasH.data;
  const tomadoresAll    = tomadoresH.data;
  const servicos        = servicosH.data;
  const solicitacoesAll = solicitacoesH.data;
  const unidadesAll     = unidadesH.data;

  // Replica ativa (URL /{slug}) — filtra pelo empresaId e (opcional) unidadeId
  const replica = useReplica(empresasAll, unidadesAll);

  // Logica de acesso:
  //  - is_admin=true                                       => acesso total
  //  - scopes vazio (ou nulo)                              => acesso total (sem restricao)
  //  - scopes contem 'principal'                           => pode acessar / (sem ser admin)
  //  - scopes com itens                                    => so pode acessar URLs daqueles slugs
  //
  // 'principal' nos scopes equivale a "tem acesso ao modo /" sem dar
  // poder de admin (que ve tudo). Util quando o admin libera SO o
  // principal pra alguem sem dar ao mesmo tempo as replicas.
  const isUnrestricted = isAdmin || scopes.length === 0;
  const hasPrincipalScope = scopes.includes('principal');
  const canAccessCurrent = isUnrestricted
    || (replica.isReplica && scopes.includes(replica.slug))
    || (!replica.isReplica && hasPrincipalScope);

  // ---------------------------------------------------------------
  // 4) Dados derivados (filtros aplicados a partir da replica)
  // ---------------------------------------------------------------
  // Modo "free empresa select": replica especial (ex: LLCs) onde o
  // usuario escolhe qualquer empresa emissora e a replica mostra so
  // o que foi criado AQUI (filtro por replica_origem, nao por empresa).
  const isFreeMode = !!replica.config?.freeEmpresaSelect;

  // Dados visiveis:
  //  - Modo principal: tudo
  //  - Replica normal: filtra pela empresaId (+ unidadeId se houver)
  //  - Replica freeMode (LLCs): nao filtra empresas/tomadores/unidades —
  //    o usuario pode escolher qualquer um. Solicitacoes ficam filtradas
  //    por replica_origem.
  //  - Modo replica mas misconfigured: mostra vazio (segurança)
  // includeNull: quando true, items com a chave nula tambem passam (usado
  // pros tomadores "cliente em todas as empresas" — vinculo opcional).
  //
  // Em freeMode (LLCs) o filtro AINDA acontece pra tomadores/unidades —
  // a replica so emite pra qualquer empresa, mas a base de clientes
  // visiveis ali continua sendo a da empresa-dona da replica + os
  // compartilhados. (A excecao e `empresas` em si, que abaixo nao usa
  // este helper e em freeMode mostra todas.)
  const filterByEmpresa = (items, key, { includeNull = false, useUnidade = false } = {}) => {
    if (!replica.isReplica) return items;
    if (replica.misconfigured) return [];
    // Quando a replica tem unidade configurada (ex: SUPERSONIC = ETUS
    // MEDIA HOLDING / Supersônic), o filtro vira POR UNIDADE em vez de
    // empresa — tomadores so aparecem se foram tagueados pra essa
    // unidade especifica. Sem unidade na config, cai no filtro normal
    // por empresa.
    if (useUnidade && replica.unidadeId) {
      return items.filter(x =>
        x.unidade_negocio_id === replica.unidadeId || (includeNull && !x[key])
      );
    }
    return items.filter(x =>
      x[key] === replica.empresaId || (includeNull && !x[key])
    );
  };
  // Empresas: em freeMode (LLCs, Plusdin) mostra TODAS (usuario emite pra
  // qualquer) OU restringe a lista se replica.config.emissoraNomes estiver
  // definido (whitelist por substring de razao_social ou nome_fantasia);
  // em replica normal so a empresa-dona da replica.
  const empresas = (() => {
    if (replica.isReplica && !isFreeMode) {
      return replica.empresa ? [replica.empresa] : [];
    }
    // freeMode ou principal
    const whitelist = replica.config?.emissoraNomes;
    if (Array.isArray(whitelist) && whitelist.length > 0) {
      return empresasAll.filter(e => whitelist.some(nome => {
        const alvo = String(nome).toUpperCase();
        return (e.razao_social  || '').toUpperCase().includes(alvo)
            || (e.nome_fantasia || '').toUpperCase().includes(alvo);
      }));
    }
    return empresasAll;
  })();
  // Tomadores:
  //  - Replica sem unidade configurada: filtra por empresa_id da replica.
  //  - Replica com unidade configurada (ex: Supersônic): filtra por
  //    unidade_negocio_id da replica — so aparecem os tomadores que
  //    foram especificamente vinculados aquela unidade.
  //  - Em ambos os casos, "cliente em todas" (empresa_id null) sempre aparece.
  const tomadores = filterByEmpresa(tomadoresAll, 'empresa_id', {
    includeNull: true,
    useUnidade: true,
  });
  // Unidades: sempre da empresa da replica. (Em freeMode o form de
  // solicitacao filtra ainda mais pela empresa selecionada no momento.)
  const unidades  = filterByEmpresa(unidadesAll, 'empresa_id');
  // Solicitacoes:
  //  - Principal: tudo.
  //  - freeMode: SO o que foi criado nesta replica (replica_origem = slug).
  //  - Replica normal: empresa_id + opcional unidade_id; EXCLUI itens
  //    tagueados pra outras replicas (pra emissoes de LLCs/etc nao
  //    vazarem aqui mesmo se a empresa bater).
  const solicitacoes = (() => {
    if (!replica.isReplica) return solicitacoesAll;
    if (isFreeMode) {
      return solicitacoesAll.filter(s => s.replica_origem === replica.slug);
    }
    if (replica.misconfigured) return [];
    let arr = solicitacoesAll.filter(s =>
      s.empresa_id === replica.empresaId &&
      (!s.replica_origem || s.replica_origem === replica.slug)
    );
    if (replica.unidadeId) {
      arr = arr.filter(s => s.unidade_negocio_id === replica.unidadeId);
    }
    return arr;
  })();

  // ---------------------------------------------------------------
  // 5) TODOS os useEffect — sempre rodam, em ordem fixa.
  //    O early return de !canAccessCurrent fica DEPOIS pra nao
  //    violar as Rules of Hooks.
  // ---------------------------------------------------------------

  // Auto-redirect: user com scopes (restrito) no Principal cai pra primeira replica.
  // Excecao: se 'principal' esta em scopes, ele PODE estar em / — nao redireciona.
  useEffect(() => {
    if (!replica.isReplica && !isUnrestricted && !hasPrincipalScope && scopes.length > 0) {
      navigate(`/${scopes.find(s => s !== 'principal') || scopes[0]}`);
    }
  }, [replica.isReplica, isUnrestricted, hasPrincipalScope, scopes]);

  // Em modo replica nao tem aba 'Empresas'; se o usuario estava nela ao
  // entrar na replica, volta pra home.
  useEffect(() => {
    if (replica.isReplica && view === 'empresas') {
      setView('home');
    }
  }, [replica.isReplica, view]);

  // Override autoritativo do tema: quando a empresa carrega do DB e tem
  // tema_cor, aplica esse valor. Modo principal (sem replica) nao mexe —
  // o effect do App() ja cuida de limpar via slug. Sem cleanup aqui pra
  // nao "zerar" o tema base que App() aplicou.
  useEffect(() => {
    if (replica.isReplica && replica.empresa?.tema_cor) {
      applyAppTheme(replica.empresa.tema_cor);
    }
  }, [replica.isReplica, replica.empresa?.tema_cor]);

  // Auto-mover pra Atrasado quando previsao_recebimento ja passou e status
  // ainda esta em Solicitada/Enviada. Roda uma vez quando os dados carregam.
  useEffect(() => {
    if (autoAtrasadoRanRef.current) return;
    if (solicitacoesH.loading || solicitacoes.length === 0) return;
    autoAtrasadoRanRef.current = true;

    const today = todayISO();
    solicitacoes
      .filter(s =>
        s.previsao_recebimento &&
        s.previsao_recebimento < today &&
        (s.status === 'Solicitada' || s.status === 'Enviada')
      )
      .forEach(s => {
        solicitacoesH.update(s.id, { status: 'Atrasado' }).catch(() => {});
      });
  }, [solicitacoesH.loading, solicitacoes, solicitacoesH]);

  // ---------------------------------------------------------------
  // 6) Early return de acesso — DEPOIS de todos os hooks
  // ---------------------------------------------------------------
  if (!canAccessCurrent) {
    return <AccessDenied email={session?.user?.email} />;
  }

  // ---------------------------------------------------------------
  // 7) Helpers (so usados pos-acesso liberado)
  // ---------------------------------------------------------------
  // Reset quando abre/fecha o modal de send-nf
  const openSendNFModal = (s) => {
    setSendNFRecipients(s?.emails_envio || '');
    setSendNFResult(null);
    setSendNFSending(false);
    setModal({ type: 'send-nf', data: s });
  };

  const errMsg = (e) => e?.message || e?.error_description || 'Erro inesperado';

  const saveEmpresa = async (data) => {
    try {
      if (data.id) {
        await empresasH.update(data.id, sanitizeEmpresa(data));
        toast('Empresa atualizada');
      } else {
        await empresasH.create(sanitizeEmpresa(data));
        toast('Empresa cadastrada');
      }
      setModal(null);
    } catch (e) { toast(errMsg(e), 'error'); }
  };
  const deleteEmpresa = async (id) => {
    try { await empresasH.remove(id); toast('Empresa excluída'); }
    catch (e) { toast(errMsg(e), 'error'); }
    setConfirmDelete(null);
  };

  const saveTomador = async (data) => {
    try {
      if (data.id) {
        await tomadoresH.update(data.id, sanitizeTomador(data));
        toast('Tomador atualizado');
      } else {
        const row = await tomadoresH.create(sanitizeTomador(data));
        toast('Tomador cadastrado');
        // Se o modal foi aberto a partir do form de solicitacao (botao
        // '+ Novo tomador'), guarda o id pro SolicitacaoForm capturar e
        // selecionar automaticamente.
        if (modal?.fromSolicitacao && row?.id) {
          setNewlyCreatedTomadorId(row.id);
        }
      }
      setModal(null);
    } catch (e) { toast(errMsg(e), 'error'); }
  };
  const deleteTomador = async (id) => {
    try { await tomadoresH.remove(id); toast('Tomador excluído'); }
    catch (e) { toast(errMsg(e), 'error'); }
    setConfirmDelete(null);
  };

  const saveServico = async (data) => {
    try {
      if (data.id) {
        await servicosH.update(data.id, sanitizeServico(data));
        toast('Serviço atualizado');
      } else {
        await servicosH.create(sanitizeServico(data));
        toast('Serviço cadastrado');
      }
      setModal(null);
    } catch (e) { toast(errMsg(e), 'error'); }
  };
  const deleteServico = async (id) => {
    try {
      await servicosH.remove(id);
      toast('Serviço excluído');
    } catch (e) {
      if (isFKViolation(e)) {
        // Em uso por solicitacoes antigas — soft-delete em vez de remover.
        try {
          await servicosH.update(id, { ativo: false });
          toast('Serviço em uso por solicitações antigas — desativado em vez de excluído');
        } catch (e2) {
          toast(errMsg(e2), 'error');
        }
      } else {
        toast(errMsg(e), 'error');
      }
    }
    setConfirmDelete(null);
  };
  const reactivateServico = async (id) => {
    try {
      await servicosH.update(id, { ativo: true });
      toast('Serviço reativado');
    } catch (e) { toast(errMsg(e), 'error'); }
  };

  const saveUnidade = async (data) => {
    try {
      if (data.id) {
        await unidadesH.update(data.id, sanitizeUnidade(data));
        toast('Unidade atualizada');
      } else {
        await unidadesH.create(sanitizeUnidade(data));
        toast('Unidade cadastrada');
      }
      setModal(null);
    } catch (e) { toast(errMsg(e), 'error'); }
  };
  const deleteUnidade = async (id) => {
    try { await unidadesH.remove(id); toast('Unidade excluída'); }
    catch (e) { toast(errMsg(e), 'error'); }
    setConfirmDelete(null);
  };

  const saveSolic = async (data) => {
    try {
      const isEdit = !!data.id;
      const serieTipo = isEdit ? 'unico' : (data.serie_tipo || 'unico');
      const serieQtd  = Math.max(1, Math.min(12, Number(data.serie_quantidade) || 1));

      // ───── EDIT — comportamento original (1 linha) ─────
      if (isEdit) {
        const row = await solicitacoesH.update(data.id, sanitizeSolic(data));
        toast('Solicitação atualizada');
        if (row?.id) logEvento(row.id, EVENTOS.EDITADA);
        setEditingSolic(null);
        setView('home');
        if (row) setModal({ type: 'pdf', data: row });
        return;
      }

      // ───── CRIACAO — 1 linha (unico) OU N linhas (recorrencia/parcelamento) ─────
      const basePayload = sanitizeSolic(data);
      if (replica.isReplica) basePayload.replica_origem = replica.slug;

      // Caso simples: lancamento unico.
      if (serieTipo === 'unico' || serieQtd <= 1) {
        const row = await solicitacoesH.create(basePayload);
        toast('Solicitação criada');
        if (row?.id) logEvento(row.id, EVENTOS.CRIADA);
        setEditingSolic(null);
        setView('home');
        if (row) setModal({ type: 'pdf', data: row });
        return;
      }

      // ───── SERIE (recorrencia ou parcelamento): N inserts em loop ─────
      const serieId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        // Fallback bem improvavel de cair aqui (todo browser moderno tem).
        : `serie-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const valorTotal = Number(data.valor) || 0;
      // Em parcelamento, divide por qtd e arredonda em centavos. A ultima
      // parcela recebe o "resto" pra fechar exatamente o valor total
      // (evita perder centavos por arredondamento).
      const valorPorItem = serieTipo === 'parcelamento'
        ? Math.round((valorTotal / serieQtd) * 100) / 100
        : valorTotal;
      const restoUltima = serieTipo === 'parcelamento'
        ? Math.round((valorTotal - (valorPorItem * (serieQtd - 1))) * 100) / 100
        : valorPorItem;

      const baseVenc = data.previsao_recebimento || null;
      const baseComp = data.data_prestacao_servico || null;

      const created = [];
      let failedAt = null;
      for (let i = 1; i <= serieQtd; i++) {
        const isLast = i === serieQtd;
        const payload = {
          ...basePayload,
          valor: isLast ? restoUltima : valorPorItem,
          previsao_recebimento: baseVenc ? addMonthsISO(baseVenc, i - 1) : null,
          data_prestacao_servico: baseComp ? addMonthsISO(baseComp, i - 1) : basePayload.data_prestacao_servico,
          serie_id: serieId,
          serie_tipo: serieTipo,
          serie_posicao: i,
          serie_total: serieQtd,
        };
        try {
          const row = await solicitacoesH.create(payload);
          if (row?.id) {
            created.push(row);
            logEvento(row.id, EVENTOS.CRIADA, {
              serie: { tipo: serieTipo, posicao: i, total: serieQtd, serie_id: serieId },
            });
          }
        } catch (err) {
          failedAt = { posicao: i, error: err };
          break;
        }
      }

      if (failedAt) {
        const label = serieTipo === 'recorrencia' ? 'repetição' : 'parcela';
        toast(
          `${created.length} de ${serieQtd} criadas, mas falhou na ${label} ${failedAt.posicao}: ${errMsg(failedAt.error)}`,
          'error'
        );
      } else {
        const label = serieTipo === 'recorrencia' ? 'repetições' : 'parcelas';
        toast(`${serieQtd} ${label} criadas`);
      }

      setEditingSolic(null);
      setView('home');
      // Abre o espelho da PRIMEIRA da serie.
      if (created[0]) setModal({ type: 'pdf', data: created[0] });
    } catch (e) { toast(errMsg(e), 'error'); }
  };
  const deleteSolic = async (id) => {
    try { await solicitacoesH.remove(id); toast('Solicitação excluída'); }
    catch (e) { toast(errMsg(e), 'error'); }
    setConfirmDelete(null);
  };
  const deleteCambio = async (id) => {
    try { await cambioH.remove(id); toast('Fechamento excluído'); }
    catch (e) { toast(errMsg(e), 'error'); }
    setConfirmDelete(null);
  };
  const updateStatus = async (s, status) => {
    try {
      const statusAnterior = s.status;
      // Payload base: sempre atualiza o status
      const patch = { status };
      // Quando muda pra 'Recebido' e ainda nao tem recebido_em, auto-preenche
      // com o timestamp atual + email do user logado. Preserva o campo quando
      // ja veio via tomador externo (fluxo espelho-confirm, que gravou
      // recebido_em quando o cliente clicou "Confirmo o recebimento da NF" e
      // pos status em 'Aguardando pagamento').
      if (status === 'Recebido' && !s.recebido_em) {
        patch.recebido_em = new Date().toISOString();
        patch.recebido_por_email = session?.user?.email || null;
      }
      await solicitacoesH.update(s.id, patch);
      toast(`Status atualizado: ${status}`);
      logEvento(s.id, EVENTOS.STATUS_ALTERADO, { de: statusAnterior, para: status });
    } catch (e) { toast(errMsg(e), 'error'); }
  };
  const attachNota = async (s, url) => {
    if (!url) return;
    try {
      const statusAnterior = s.status;
      await solicitacoesH.update(s.id, { anexo_nota_url: url, status: 'Enviada' });
      toast('Nota emitida anexada');
      logEvento(s.id, EVENTOS.NF_ANEXADA, { de: statusAnterior, para: 'Enviada' });
      setModal(null);
    } catch (e) { toast(errMsg(e), 'error'); }
  };

  const counts = {
    solicitacoes: solicitacoes.length,
    empresas: empresas.length,
    unidades: unidades.length,
    tomadores: tomadores.length,
    servicos: servicos.length,
    cambio: cambioH.data?.length || 0,
  };

  return (
    <div style={{
      display: 'flex',
      height: '100vh', background: 'var(--bg)', color: 'var(--ink)',
    }}>
      {/* Sidebar — full height, da topo ate o rodape do viewport */}
      <div className="no-print" style={{
        width: SIDEBAR_COLLAPSED_WIDTH,
        flexShrink: 0,
        position: 'relative',
      }}>
        <Sidebar
          current={view === 'nova' ? 'nova' : view}
          setCurrent={(v) => {
            setEditingSolic(null);
            // Quando troca de modulo, volta o cambio sub-view pra home
            if (v !== 'cambio') setCambioSubView('home');
            setView(v);
          }}
          counts={counts}
          userEmail={session?.user?.email}
          hideEmpresas={replica.isReplica}
          legacyApps={
            // Em replica: um item so com o link da config daquela replica.
            // Em principal: a lista completa de LEGACY_APPS (constants.js).
            replica.isReplica
              ? [{ label: 'Versão anterior', url: replica.config?.legacyUrl }]
              : LEGACY_APPS
          }
          // Onboarding: em replica usa o campo onboardingUrl da config;
          // em principal usa a constante global PRINCIPAL_ONBOARDING_URL.
          onboardingUrl={
            replica.isReplica
              ? (replica.config?.onboardingUrl || null)
              : (PRINCIPAL_ONBOARDING_URL || null)
          }
          // Liga o item "Câmbio" no menu — hoje so a replica LLCs.
          hasCambioModule={!!replica.config?.hasCambioModule}
        />
      </div>

      {/* Coluna direita: header + main scrollavel */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div className="no-print">
          <AppHeader replica={replica} isAdmin={isAdmin} scopes={scopes} />
        </div>

      <main className="scrollbar" style={{
        flex: 1, overflowY: 'auto', padding: '32px 36px 48px',
      }}>
        <div style={{ maxWidth: 1600, margin: '0 auto' }}>
          {view === 'home' && (
            <HomeView
              solicitacoes={solicitacoes}
              empresas={empresas}
              tomadores={tomadores}
              loading={solicitacoesH.loading}
              onNew={() => { setEditingSolic(null); setView('nova'); }}
              onEdit={(s) => { setEditingSolic(s); setView('nova'); }}
              // Duplicar: copia os campos do lancamento atual mas zera os
              // que sao especificos do registro original (id pra create
              // virar create, codigo_unico/numero_nota/anexo/status/replica
              // pra nao herdar; data_solicitacao = hoje). Quem usa: criar
              // varios lancamentos repetitivos sem digitar tudo de novo.
              onCopy={(s) => {
                const copy = {
                  ...s,
                  id: undefined,
                  codigo_unico: undefined,
                  numero_nota: '',
                  anexo_nota_url: '',
                  anexo_intermediario_url: '',
                  pdf_espelho_url: '',
                  status: 'Solicitada',
                  data_solicitacao: todayISO(),
                  replica_origem: undefined,
                  created_at: undefined,
                  updated_at: undefined,
                  created_by: undefined,
                  __isCopy: true,
                };
                setEditingSolic(copy);
                setView('nova');
              }}
              onView={(s) => setModal({ type: 'detail', data: s })}
              onPdf={(s) => setModal({ type: 'pdf', data: s })}
              onAttach={(s) => setModal({ type: 'attach', data: s })}
              onSendNF={openSendNFModal}
              onDelete={(s) => setConfirmDelete({ type: 'solic', item: s })}
              onStatusChange={updateStatus}
            />
          )}

          {view === 'nova' && (
            <SolicitacaoForm
              initial={editingSolic}
              empresas={empresas}
              tomadores={tomadores}
              servicos={servicos}
              unidades={unidades}
              forceExterior={!!replica.config?.forceExterior}
              onSubmit={saveSolic}
              onCancel={() => { setEditingSolic(null); setView('home'); }}
              onError={(msg) => toast(msg, 'error')}
              // Abre o modal de cadastro de tomador a partir do form;
              // a flag fromSolicitacao=true faz o saveTomador devolver o
              // id do tomador recem-criado pra o form auto-selecionar.
              onCreateTomador={() => setModal({ type: 'tomador', data: null, fromSolicitacao: true })}
              newlyCreatedTomadorId={newlyCreatedTomadorId}
              onConsumeNewTomadorId={() => setNewlyCreatedTomadorId(null)}
              // Trava empresa+unidade quando a replica define os dois
              // (ex: Supersonic = ETUS MEDIA + Supersonic). Em freeMode
              // (LLCs), nao trava — o usuario escolhe a emissora livremente.
              lockedEmpresaId={(replica.isReplica && !isFreeMode) ? replica.empresaId : null}
              lockedUnidadeId={(replica.isReplica && !isFreeMode) ? replica.unidadeId : null}
              // Em modo Exterior: libera digitar o R$ direto sem PTAX+USD
              // (mantem auto-calculo quando os 2 sao informados). Hoje so BHAZ.
              freeExteriorValue={!!replica.config?.freeExteriorValue}
              // Esconde o picker de recorrencia/parcelamento (hoje so BHAZ).
              // Pra reativar: tirar a flag hideSerieControls da config da BHAZ.
              hideSerieControls={!!replica.config?.hideSerieControls}
              // BHAZ: substitui 'Documento auxiliar' por 2 slots complementares
              // + comprime PDFs no browser antes de subir pro storage.
              useAnexosComplementares={!!replica.config?.useAnexosComplementares}
              compressPdfUploads={!!replica.config?.compressPdfUploads}
              useDocsAuxiliaresMulti={!!replica.config?.useDocsAuxiliaresMulti}
              useNotaPILayout={!!replica.config?.useNotaPILayout}
            />
          )}

          {view === 'empresas' && (
            <EmpresasView
              empresas={empresas}
              loading={empresasH.loading}
              onAdd={() => setModal({ type: 'empresa', data: null })}
              onEdit={(e) => setModal({ type: 'empresa', data: e })}
              onDelete={(e) => setConfirmDelete({ type: 'empresa', item: e })}
            />
          )}

          {view === 'unidades' && (
            <UnidadesView
              unidades={unidadesAll}
              empresas={empresasAll}
              loading={unidadesH.loading}
              onAdd={() => setModal({ type: 'unidade', data: null })}
              onEdit={(u) => setModal({ type: 'unidade', data: u })}
              onDelete={(u) => setConfirmDelete({ type: 'unidade', item: u })}
            />
          )}

          {view === 'tomadores' && (
            <TomadoresView
              tomadores={tomadores}
              empresas={empresas}
              unidades={unidadesAll}
              loading={tomadoresH.loading}
              onAdd={() => setModal({ type: 'tomador', data: null })}
              onEdit={(t) => setModal({ type: 'tomador', data: t })}
              onDelete={(t) => setConfirmDelete({ type: 'tomador', item: t })}
            />
          )}

          {view === 'servicos' && (
            <ServicosView
              servicos={servicos}
              loading={servicosH.loading}
              onAdd={() => setModal({ type: 'servico', data: null })}
              onEdit={(s) => setModal({ type: 'servico', data: s })}
              onDelete={(s) => setConfirmDelete({ type: 'servico', item: s })}
              onReactivate={(s) => reactivateServico(s.id)}
            />
          )}

          {view === 'cambio' && cambioSubView === 'home' && (
            <CambioHome
              fechamentos={cambioH.data}
              loading={cambioH.loading}
              onNew={() => { setEditingCambio(null); setCambioSubView('form'); }}
              onView={(c) => setCambioModal({ data: c })}
              onEdit={(c) => { setEditingCambio(c); setCambioSubView('form'); }}
              onDelete={(c) => setConfirmDelete({ type: 'cambio', item: c })}
              onDuplicate={(c) => {
                // Clona o fechamento pra um novo (sem id e sem codigo_unico)
                const copy = { ...c };
                delete copy.id;
                delete copy.codigo_unico;
                delete copy.created_at;
                delete copy.updated_at;
                copy.status = 'aberto';
                copy.observacao = `Duplicado de ${c.codigo_unico}`;
                setEditingCambio(copy);
                setCambioSubView('form');
              }}
              onLiquidar={async (c) => {
                try {
                  await cambioH.update(c.id, { status: 'liquidado', liquidado_em: new Date().toISOString() });
                  toast(`${c.codigo_unico} marcado como liquidado`);
                } catch (e) { toast(errMsg(e), 'error'); }
              }}
            />
          )}

          {view === 'cambio' && cambioSubView === 'form' && (
            <CambioForm
              initial={editingCambio}
              onCancel={() => { setEditingCambio(null); setCambioSubView('home'); }}
              onError={(msg) => toast(msg, 'error')}
              onSubmit={async (payload) => {
                try {
                  if (editingCambio?.id) {
                    await cambioH.update(editingCambio.id, payload);
                    toast('Fechamento atualizado');
                  } else {
                    await cambioH.create(payload);
                    toast('Fechamento criado');
                  }
                  setEditingCambio(null);
                  setCambioSubView('home');
                } catch (err) {
                  toast(err?.message || 'Erro ao salvar', 'error');
                }
              }}
            />
          )}
        </div>
      </main>
      </div>

      <Modal
        open={modal?.type === 'empresa'}
        onClose={() => setModal(null)}
        title={modal?.data ? 'Editar empresa' : 'Nova empresa'}
        subtitle="Informe o CNPJ para preenchimento automático"
        size="lg"
      >
        <CNPJEntityForm
          initial={modal?.data}
          empresas={empresas}
          isTomador={false}
          onSubmit={saveEmpresa}
          onCancel={() => setModal(null)}
        />
      </Modal>

      <Modal
        open={modal?.type === 'tomador'}
        onClose={() => setModal(null)}
        title={modal?.data ? 'Editar tomador' : 'Novo tomador'}
        subtitle="Informe o CNPJ para preenchimento automático"
        size="lg"
      >
        <CNPJEntityForm
          initial={modal?.data}
          empresas={empresas}
          unidades={unidadesAll}
          isTomador={true}
          isReplicaMode={replica.isReplica && !isFreeMode}
          lockedEmpresaId={(replica.isReplica && !isFreeMode) ? replica.empresaId : null}
          lockedUnidadeId={(replica.isReplica && !isFreeMode) ? replica.unidadeId : null}
          // Replica com allowTomadorPF (hoje: BHAZ) ganha uma 3a aba 'PF'
          // pra cadastrar cliente Pessoa Fisica com CPF em vez de CNPJ.
          allowPessoaFisica={!!replica.config?.allowTomadorPF}
          onSubmit={saveTomador}
          onCancel={() => setModal(null)}
        />
      </Modal>

      <Modal
        open={modal?.type === 'servico'}
        onClose={() => setModal(null)}
        title={modal?.data ? 'Editar serviço' : 'Novo serviço'}
      >
        <ServicoForm
          initial={modal?.data}
          onSubmit={saveServico}
          onCancel={() => setModal(null)}
        />
      </Modal>

      <Modal
        open={modal?.type === 'unidade'}
        onClose={() => setModal(null)}
        title={modal?.data ? 'Editar unidade de negócio' : 'Nova unidade de negócio'}
      >
        <UnidadeNegocioForm
          initial={modal?.data}
          empresas={empresasAll}
          onSubmit={saveUnidade}
          onCancel={() => setModal(null)}
        />
      </Modal>

      {modal?.type === 'pdf' && (
        <PDFEspelho
          open={true}
          onClose={() => setModal(null)}
          solicitacao={modal.data}
          empresa={empresas.find(e => e.id === modal.data.empresa_id)}
          tomador={tomadores.find(t => t.id === modal.data.tomador_id)}
          servico={servicos.find(s => s.id === modal.data.servico_id)}
          onToast={toast}
          // Per-replica: desabilita o botao 'Enviar por e-mail' do espelho
          // quando a config marcar disableEspelhoEmail. Hoje so Supersonic.
          disableEspelhoEmail={!!replica.config?.disableEspelhoEmail}
          // Override do remetente (display name) e do prefixo do assunto
          // do email do espelho. Hoje so BHAZ usa.
          emailFromName={replica.config?.emailFromName || ''}
          emailSubjectPrefix={replica.config?.emailSubjectPrefix || ''}
          // Mostra a caixa amarela de aviso sobre forma de pagamento no
          // espelho (PDF + email). Per-replica; hoje so BHAZ.
          showAvisoFormaPagamento={!!replica.config?.showAvisoFormaPagamento}
          // Slug da replica — server le RESEND_FROM_<SLUG> e troca o
          // endereco do remetente do email do espelho quando a env existe.
          replicaSlug={replica.slug || ''}
          // BHAZ: substitui o codigo OS-XXXXXXXX pela PI no subject do email
          // do espelho (e adiciona '· PI <n>' no subject da NF emitida).
          usePIInSubject={!!replica.config?.usePIInSubject}
        />
      )}

      {modal?.type === 'detail' && (
        <DetailsModal
          open={true}
          onClose={() => setModal(null)}
          solicitacao={modal.data}
          empresa={empresas.find(e => e.id === modal.data.empresa_id)}
          tomador={tomadores.find(t => t.id === modal.data.tomador_id)}
          servico={servicos.find(s => s.id === modal.data.servico_id)}
          onPdf={(s) => setModal({ type: 'pdf', data: s })}
          onEdit={(s) => { setEditingSolic(s); setView('nova'); setModal(null); }}
        />
      )}

      {cambioModal && (
        <CambioDetailsModal
          open={true}
          onClose={() => setCambioModal(null)}
          fechamento={cambioModal.data}
          onEdit={(c) => { setEditingCambio(c); setCambioSubView('form'); }}
        />
      )}

      <Modal
        open={modal?.type === 'attach'}
        onClose={() => setModal(null)}
        title="Anexar nota emitida"
        subtitle={`Solicitação ${modal?.data?.codigo_unico || ''}`}
      >
        <Field label="Arquivo da nota fiscal" helper="Somente PDF">
          <FileInput
            value=""
            onChange={(url) => url && attachNota(modal.data, url)}
            onError={(msg) => toast(msg, 'error')}
          />
        </Field>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 12,
          padding: 10, background: 'var(--surface-2)', borderRadius: 8 }}>
          Ao anexar, a solicitação será marcada automaticamente como <strong>Emitida</strong>.
        </div>
      </Modal>

      {/* Enviar NF emitida por e-mail */}
      {modal?.type === 'send-nf' && (() => {
        const s = modal.data;
        const empresa = empresas.find(e => e.id === s.empresa_id);
        const tomador = tomadores.find(t => t.id === s.tomador_id);
        const servico = servicos.find(sv => sv.id === s.servico_id);

        const handleMailto = () => {
          if (!sendNFRecipients.trim()) return;
          if (!validateEmailList(sendNFRecipients)) {
            setSendNFResult({ ok: false, msg: 'Algum e-mail está inválido.' });
            return;
          }
          const list = sendNFRecipients.split(/[,;]+/).map(x => x.trim()).filter(Boolean);
          openMailtoNFEmitida({ solicitacao: s, empresa, tomador, servico, to: list });
          setSendNFResult({ ok: true, msg: 'Aberto no seu cliente de e-mail. Revise e envie.' });
        };
        const handleSendServer = async () => {
          if (!sendNFRecipients.trim()) return;
          if (!validateEmailList(sendNFRecipients)) {
            setSendNFResult({ ok: false, msg: 'Algum e-mail está inválido.' });
            return;
          }
          setSendNFSending(true);
          setSendNFResult(null);
          try {
            const list = sendNFRecipients.split(/[,;]+/).map(x => x.trim()).filter(Boolean);
            const r = await sendNFEmitidaEmail({
              solicitacao: s, empresa, tomador, servico, to: list,
              fromName: replica.config?.emailFromName || undefined,
              replicaSlug: replica.slug || undefined,
              usePIInSubject: !!replica.config?.usePIInSubject,
              showReceiptCTAOnTop: !!replica.config?.showReceiptCTAOnTop,
            });
            setSendNFResult({ ok: true, msg: `Enviado para ${r.to.join(', ')}.` });
            // Auditoria do envio.
            logEvento(s.id, EVENTOS.NF_EMITIDA_ENVIADA, { destinatarios: r.to });
            // Apos envio bem-sucedido pelo servidor, marca a solicitacao
            // como 'Enviada' automaticamente — o usuario nao precisa
            // mais ir ate o status pickern depois.
            if (s.status !== 'Enviada') {
              try {
                await solicitacoesH.update(s.id, { status: 'Enviada' });
                logEvento(s.id, EVENTOS.STATUS_ALTERADO, { de: s.status, para: 'Enviada', automatico: true });
              } catch (e) {
                // Falha so no update de status nao deve ofuscar o envio
                // bem-sucedido — loga e segue.
                console.warn('[send-nf] envio ok, mas falhou ao atualizar status:', e);
              }
            }
          } catch (err) {
            setSendNFResult({ ok: false, msg: err?.message || 'Falha no envio.' });
          } finally {
            setSendNFSending(false);
          }
        };

        return (
          <Modal
            open={true}
            onClose={() => !sendNFSending && setModal(null)}
            title="Enviar NF emitida por e-mail"
            subtitle={`Solicitação ${s.codigo_unico}${s.numero_nota ? ' · NF ' + s.numero_nota : ''}`}
            footer={
              <>
                <Button variant="secondary" disabled={sendNFSending} onClick={() => setModal(null)}>Cancelar</Button>
                <Button variant="secondary" icon={ExternalLink} onClick={handleMailto}
                  disabled={sendNFSending || !sendNFRecipients.trim()}>
                  Abrir no meu e-mail
                </Button>
                <Button icon={sendNFSending ? Loader2 : Send} onClick={handleSendServer}
                  disabled={sendNFSending || !sendNFRecipients.trim()}>
                  {sendNFSending ? 'Enviando…' : 'Enviar pelo servidor'}
                </Button>
              </>
            }
          >
            <Field label="Destinatários" helper="Separe múltiplos e-mails por vírgula. O e-mail incluirá link pro arquivo da NF.">
              <input
                className="input"
                type="text"
                value={sendNFRecipients}
                onChange={e => setSendNFRecipients(e.target.value)}
                placeholder="financeiro@empresa.com, fiscal@empresa.com"
                disabled={sendNFSending}
              />
            </Field>

            <div style={{
              marginTop: 12, fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.55,
              padding: '10px 12px', background: 'var(--surface-2)',
              border: '1px solid var(--border)', borderRadius: 8,
            }}>
              O e-mail confirma que a NF foi <strong>emitida</strong> conforme espelho aprovado, e inclui um botão pro destinatário <strong>baixar/visualizar</strong> o arquivo da nota fiscal anexado.
            </div>

            {!s.anexo_nota_url && (
              <div style={{
                marginTop: 12, fontSize: 12.5, padding: '10px 12px', borderRadius: 8,
                background: 'var(--warning-soft)', color: 'var(--warning)',
                border: '1px solid rgba(255,170,51,0.4)',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <AlertCircle size={14} />
                <span>Atenção: nenhum arquivo anexado ainda. O e-mail vai sair sem o link da NF.</span>
              </div>
            )}

            {sendNFResult && (
              <div style={{
                marginTop: 12, fontSize: 12.5, padding: '10px 12px', borderRadius: 8,
                background: sendNFResult.ok ? 'var(--primary-soft)' : 'var(--danger-soft)',
                color: sendNFResult.ok ? 'var(--primary)' : 'var(--danger)',
                border: `1px solid ${sendNFResult.ok ? 'var(--primary-line)' : 'rgba(255,80,80,0.35)'}`,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                {sendNFResult.ok ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
                <span>{sendNFResult.msg}</span>
              </div>
            )}
          </Modal>
        );
      })()}

      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Confirmar exclusão"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
            <Button variant="primary"
              style={{ background: 'var(--danger)', borderColor: 'var(--danger)' }}
              icon={Trash2}
              onClick={() => {
                if (!confirmDelete) return;
                if (confirmDelete.type === 'empresa') deleteEmpresa(confirmDelete.item.id);
                if (confirmDelete.type === 'tomador') deleteTomador(confirmDelete.item.id);
                if (confirmDelete.type === 'servico') deleteServico(confirmDelete.item.id);
                if (confirmDelete.type === 'unidade') deleteUnidade(confirmDelete.item.id);
                if (confirmDelete.type === 'solic')   deleteSolic(confirmDelete.item.id);
                if (confirmDelete.type === 'cambio')  deleteCambio(confirmDelete.item.id);
              }}>
              Excluir
            </Button>
          </>
        }
      >
        <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5 }}>
          Tem certeza que deseja excluir este registro?
          <div style={{ marginTop: 8, padding: 10, background: 'var(--surface-2)',
            border: '1px solid var(--border)', borderRadius: 8, fontWeight: 600 }}>
            {confirmDelete?.item?.razao_social ||
             confirmDelete?.item?.nome ||
             confirmDelete?.item?.descricao ||
             confirmDelete?.item?.codigo_unico}
          </div>
          <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--muted)' }}>
            Esta ação não pode ser desfeita.
          </div>
        </div>
      </Modal>

      <Toast toasts={toasts} dismiss={dismiss} />
    </div>
  );
}

// whitelist por tabela — o form compartilhado guarda campos extras no state
// (empresa_id pra tomador, logo_url pra empresa) que não existem na outra tabela.

const EMPRESA_COLS = [
  'cnpj', 'razao_social', 'nome_fantasia', 'cnae_principal',
  'logradouro', 'numero', 'bairro', 'cidade', 'uf', 'cep',
  'email', 'telefone', 'inscricao_municipal', 'inscricao_estadual',
  'logo_url', 'tema_cor',
  'is_exterior', 'endereco_exterior',
];

const TOMADOR_COLS = [
  'empresa_id', 'unidade_negocio_id',
  'cnpj', 'razao_social', 'nome_fantasia', 'cnae_principal',
  'logradouro', 'numero', 'bairro', 'cidade', 'uf', 'cep',
  'email', 'telefone', 'inscricao_municipal', 'inscricao_estadual',
  'is_exterior', 'endereco_exterior',
];

const SERVICO_COLS = [
  'codigo', 'descricao',
  'aliquota_iss', 'aliquota_pis', 'aliquota_cofins',
  'aliquota_csll', 'aliquota_ir', 'aliquota_inss',
  'ativo',
];

// Postgres FK violation code. Quando o servico/empresa/tomador esta em
// uso por solicitacoes antigas, o on delete restrict bloqueia. A gente
// detecta isso pra cair pro soft-delete (servicos) ou pro toast amigavel.
const isFKViolation = (e) =>
  e?.code === '23503' ||
  /foreign key|violates foreign key|on table/i.test(String(e?.message || ''));

const SOLIC_COLS = [
  'pi', 'empresa_id', 'tomador_id', 'servico_id', 'unidade_negocio_id',
  'data_solicitacao', 'data_prestacao_servico', 'previsao_recebimento',
  'valor', 'descricao', 'solicitante', 'numero_nota',
  'impostos_retidos', 'informacoes_complementares',
  'anexo_nota_url', 'emails_envio',
  'possui_intermediario',
  'intermediario_cnpj', 'intermediario_razao_social',
  'anexo_intermediario_url',
  'anexo_complementar_1_url', 'anexo_complementar_2_url',
  // Multi-anexos (BHAZ) — array jsonb de { url, filename }
  'documentos_auxiliares',
  'pdf_espelho_url', 'status',
  'is_exterior', 'ptax', 'valor_dolar',
  'replica_origem',
  // Data de recebimento (editavel no form). Preenchida pelo
  // /api/espelho-confirm quando o cliente confirma a NF (status vai pra
  // 'Aguardando pagamento'); OU auto-preenchida ao mudar status pra
  // 'Recebido' se ainda estiver vazia. User pode ajustar manualmente.
  'recebido_em',
  // Campos de serie (recorrencia/parcelamento) — gravados em cada linha
  // gerada quando o user marca recorrencia ou parcelamento no form.
  // Pra lancamento unico, ficam todos NULL.
  'serie_id', 'serie_tipo', 'serie_posicao', 'serie_total',
];

const UNIDADE_COLS = ['empresa_id', 'nome', 'logo_url'];

const pick = (obj, keys) => {
  const out = {};
  for (const k of keys) if (obj[k] !== undefined) out[k] = obj[k];
  return out;
};

// String vazia/whitespace -> null. Importante pra cnpj/endereco_exterior:
// o unique parcial no banco trata '' como valor (e bloqueia duplicatas);
// queremos que multiplos exteriors sem identificador convivam usando NULL.
const emptyToNull = (v) => {
  if (typeof v !== 'string') return v;
  const trimmed = v.trim();
  return trimmed === '' ? null : v;
};

function sanitizeEmpresa(d) {
  const out = pick(d, EMPRESA_COLS);
  if (out.cnpj !== undefined) out.cnpj = emptyToNull(out.cnpj);
  if (out.endereco_exterior !== undefined) out.endereco_exterior = emptyToNull(out.endereco_exterior);
  return out;
}

function sanitizeTomador(d) {
  const out = pick(d, TOMADOR_COLS);
  if (out.cnpj !== undefined) out.cnpj = emptyToNull(out.cnpj);
  if (out.endereco_exterior !== undefined) out.endereco_exterior = emptyToNull(out.endereco_exterior);
  // empresa_id e opcional — vazio significa "cliente em todas".
  if (out.empresa_id !== undefined) out.empresa_id = emptyToNull(out.empresa_id);
  // unidade_negocio_id sem vinculo tambem vai como null.
  if (out.unidade_negocio_id !== undefined) out.unidade_negocio_id = emptyToNull(out.unidade_negocio_id);
  return out;
}

function sanitizeServico(d) {
  return pick(d, SERVICO_COLS);
}

function sanitizeUnidade(d) {
  return pick(d, UNIDADE_COLS);
}

function sanitizeSolic(d) {
  const out = pick(d, SOLIC_COLS);
  if (out.valor !== undefined) out.valor = Number(out.valor);
  // pi é opcional — manda null se vazio em vez de '' (fica feio no banco)
  if (out.pi !== undefined) out.pi = (typeof out.pi === 'string' ? out.pi.trim() : '') || null;
  // previsao_recebimento e date — string vazia quebra o cast; manda null
  if (out.previsao_recebimento !== undefined) {
    out.previsao_recebimento = (typeof out.previsao_recebimento === 'string'
      ? out.previsao_recebimento.trim() : '') || null;
  }
  // Exterior: ptax e valor_dolar viram Number; vazio/0/null vai como NULL
  // pra nao poluir o banco quando a solicitacao e do Brasil.
  if (out.ptax !== undefined) {
    const n = Number(out.ptax);
    out.ptax = Number.isFinite(n) && n > 0 ? n : null;
  }
  if (out.valor_dolar !== undefined) {
    const n = Number(out.valor_dolar);
    out.valor_dolar = Number.isFinite(n) && n > 0 ? n : null;
  }
  // 'unico' eh sentinela do form pra dizer "nao eh serie" — DB so aceita
  // null|'recorrencia'|'parcelamento' (check constraint solicitacoes_serie_tipo_check).
  // Em lancamento avulso o saveSolic do loop de serie sobrescreve esse campo,
  // entao normalizar pra null aqui eh seguro tanto em create quanto em edit.
  if (out.serie_tipo !== undefined && (out.serie_tipo === 'unico' || out.serie_tipo === '')) {
    out.serie_tipo = null;
  }
  return out;
}
